import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import '../Admin.css'; 

function Orders() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]); // Filter ചെയ്ത ഡാറ്റ കാണിക്കാൻ
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");

  useEffect(() => {
    const q = query(collection(db, "bookings"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(data);
      setFilteredOrders(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Search and Filter Logic
  useEffect(() => {
    let result = orders;

    // 1. Search (ID, Name, Phone)
    if (searchTerm) {
      result = result.filter(order => 
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.customerName && order.customerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.customerPhone && order.customerPhone.includes(searchTerm))
      );
    }

    // 2. Status Filter
    if (statusFilter !== "All") {
      result = result.filter(order => (order.orderStatus || 'Pending') === statusFilter);
    }

    // 3. Date Filter
    if (dateFilter) {
      result = result.filter(order => {
        const orderDate = order.createdAt?.toDate().toISOString().split('T')[0];
        return orderDate === dateFilter;
      });
    }

    setFilteredOrders(result);
  }, [searchTerm, statusFilter, dateFilter, orders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, "bookings", orderId);
      await updateDoc(orderRef, { orderStatus: newStatus });
    } catch (error) {
      console.error("Status Update Error:", error);
    }
  };

  if (loading) return <div className="admin-loader">Loading Orders...</div>;

  return (
    <div className="orders-container">
      
      {/* --- SEARCH & FILTER SECTION --- */}
      <div className="filter-bar">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input 
            type="text" 
            placeholder="Search by ID, Name or Phone..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-options">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Picked Up">Picked Up</option>
            <option value="Processing">Processing</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          <input 
            type="date" 
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
          
          {(searchTerm || statusFilter !== "All" || dateFilter) && (
            <button className="clear-btn" onClick={() => {setSearchTerm(""); setStatusFilter("All"); setDateFilter("");}}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td className="order-id-cell">#{order.id.slice(0, 8).toUpperCase()}</td>
                  <td><strong>{order.customerName || "Guest"}</strong></td>
                  <td>{order.customerPhone}</td>
                  <td>{order.createdAt?.toDate().toLocaleDateString()}</td>
                  <td>AED {order.payment?.totalPayable}</td>
                  <td>
                    <span className={`status-badge ${order.orderStatus?.toLowerCase().replace(/\s/g, '-') || 'pending'}`}>
                      {order.orderStatus || 'Pending'}
                    </span>
                  </td>
                  <td>
                    <div className="action-btns">
                      <button className="view-btn-icon" onClick={() => setSelectedOrder(order)} title="View Details">
                        <i className="fas fa-eye"></i>
                      </button>
                      <select 
                        className="status-minimal-select"
                        value={order.orderStatus || 'Pending'}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Picked Up">Picked Up</option>
                        <option value="Processing">Processing</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{textAlign: 'center', padding: '30px', color: '#64748b'}}>No orders found matches your filter.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- ENHANCED ORDER DETAILS MODAL (No Changes Here) --- */}
      {selectedOrder && (
        <div className="modal-overlay">
          <div className="modal-card animate-in">
            <div className="modal-header-premium">
              <div>
                <h2>Order Details</h2>
                <p>ID: #{selectedOrder.id.toUpperCase()}</p>
              </div>
              <button className="close-circle-btn" onClick={() => setSelectedOrder(null)}>&times;</button>
            </div>

            <div className="modal-body-scroll">
              <div className="info-grid">
                <div className="info-box">
                  <h4><i className="fas fa-user"></i> Customer Info</h4>
                  <p><strong>Name:</strong> {selectedOrder.customerName}</p>
                  <p><strong>Phone:</strong> {selectedOrder.customerPhone}</p>
                </div>
                <div className="info-box">
                  <h4><i className="fas fa-map-marker-alt"></i> Shipping Address</h4>
                  <p>
                    {selectedOrder.address?.villa || 'N/A'}, 
                    {selectedOrder.address?.street ? ` ${selectedOrder.address.street},` : ''} 
                    {selectedOrder.address?.city || ''}
                  </p>
                  <p className="address-subtext">Landmark: {selectedOrder.address?.landmark || 'None'}</p>
                  <p className="address-subtext" style={{marginTop: '5px', color: '#1e293b'}}>
                    <small><i className="fas fa-user"></i> {selectedOrder.address?.userName}</small> | 
                    <small> <i className="fas fa-phone"></i> {selectedOrder.address?.phone}</small>
                  </p>
                </div>
              </div>

              <div className="info-grid mt-3">
                <div className="info-box border-blue">
                  <h4><i className="fas fa-calendar-alt"></i> Pickup Schedule</h4>
                  <p>Date: {selectedOrder.scheduling?.pickup?.date}</p>
                  <p>Slot: {selectedOrder.scheduling?.pickup?.time}</p>
                </div>
                <div className="info-box border-green">
                  <h4><i className="fas fa-truck"></i> Delivery Schedule</h4>
                  <p>Expected: {selectedOrder.scheduling?.dropoff?.date}</p>
                  <p>Slot: {selectedOrder.scheduling?.dropoff?.time}</p>
                </div>
              </div>

              <div className="modal-items-container">
                <h4>Items Ordered</h4>
                <div className="items-header">
                  <span>Item</span>
                  <span>Qty</span>
                  <span>Price</span>
                </div>
                {selectedOrder.cart?.map((item, idx) => (
                  <div key={idx} className="item-row-data">
                    <span>{item.name}</span>
                    <span>{item.quantity}</span>
                    <span>AED {(item.offerPrice * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="payment-summary">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>AED {selectedOrder.payment?.totalPayable}</span>
                  </div>
                  <div className="summary-row grand-total">
                    <span>Grand Total</span>
                    <span>AED {selectedOrder.payment?.totalPayable}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
               <button className="print-btn" onClick={() => window.print()}>
                 <i className="fas fa-print"></i> Print Invoice
               </button>
               <button className="done-btn" onClick={() => setSelectedOrder(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Orders;